import { env } from "@/redux/http";

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: any;
  timestamp: number;
  context?: string;
}

class Logger {
  private readonly isDevelopment = env === "dev";
  private isDebugEnabled = this.isDevelopment;
  private logBuffer: LogEntry[] = [];
  private readonly maxBufferSize = this.isDevelopment ? 100 : 50;

  private log(level: LogLevel, context: string, message: string, data?: any) {
    // In production, only store warn/error logs in buffer unless debug is enabled
    const shouldBuffer =
      this.isDevelopment ||
      this.isDebugEnabled ||
      level === "warn" ||
      level === "error";

    if (shouldBuffer) {
      this.addToBuffer({
        level,
        message,
        data,
        timestamp: Date.now(),
        context,
      });
    }

    // Only output to console in development or if debug is enabled
    if (!this.isDebugEnabled && level !== "error") {
      return;
    }

    const prefix = `[${context}]`;

    switch (level) {
      case "debug":
        if (this.isDevelopment) console.debug(prefix, message, data);
        break;
      case "info":
        console.log(prefix, message, data);
        break;
      case "warn":
        console.warn(prefix, message, data);
        break;
      case "error":
        console.error(prefix, message, data);
        break;
    }
  }

  private addToBuffer(entry: LogEntry) {
    this.logBuffer.push(entry);

    // Keep buffer size limited
    if (this.logBuffer.length > this.maxBufferSize) {
      this.logBuffer.shift();
    }
  }

  // Context-specific loggers
  debug(context: string, message: string, data?: any) {
    this.log("debug", context, message, data);
  }

  info(context: string, message: string, data?: any) {
    this.log("info", context, message, data);
  }

  warn(context: string, message: string, data?: any) {
    this.log("warn", context, message, data);
  }

  error(context: string, message: string, data?: any) {
    this.log("error", context, message, data);
  }

  // Enable/disable debug mode at runtime
  setDebugMode(enabled: boolean) {
    this.isDebugEnabled = enabled;
    localStorage.setItem("debug", enabled ? "true" : "false");
  }

  // Get recent logs for debugging
  getRecentLogs(count: number = 100): LogEntry[] {
    return this.logBuffer.slice(-count);
  }

  // Export logs for remote debugging
  exportLogs(): string {
    return JSON.stringify(this.logBuffer, null, 2);
  }

  // Clear log buffer
  clearLogs() {
    this.logBuffer = [];
  }

  // Get approximate memory usage of log buffer
  getBufferInfo() {
    const bufferSize = JSON.stringify(this.logBuffer).length;
    return {
      count: this.logBuffer.length,
      maxCount: this.maxBufferSize,
      approximateSizeKB: Math.round(bufferSize / 1024),
      oldestLog: this.logBuffer[0]?.timestamp,
      newestLog: this.logBuffer[this.logBuffer.length - 1]?.timestamp,
    };
  }
}

export const logger = new Logger();
